import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Monitor } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDesksPage() {
  const { t } = useLanguage();
  const [desks, setDesks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDesks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/desks`, { withCredentials: true });
      setDesks(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDesks();
      setLoading(false);
    };
    load();
  }, [fetchDesks]);

  const handleDeskToggle = async (deskId, isAvailable) => {
    try {
      await axios.put(`${API_URL}/api/admin/desks/${deskId}`, { is_available: isAvailable }, { withCredentials: true });
      await fetchDesks();
      toast.success(isAvailable ? t('deskActivated', { desk: deskId }) : t('deskDeactivated', { desk: deskId }));
    } catch {
      toast.error(t('error'));
    }
  };

  const handleDeskMonitorToggle = async (deskId, hasMonitor) => {
    try {
      await axios.put(`${API_URL}/api/admin/desks/${deskId}`, { has_monitor: hasMonitor }, { withCredentials: true });
      await fetchDesks();
    } catch {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  const handleWeekdayToggle = async (deskId, dayIndex, currentWeekdays) => {
    const next = currentWeekdays.includes(dayIndex)
      ? currentWeekdays.filter((d) => d !== dayIndex)
      : [...currentWeekdays, dayIndex].sort();
    if (next.length === 0) {
      toast.error(t('weekdaysMinOne'));
      return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/desks/${deskId}`, { available_weekdays: next }, { withCredentials: true });
      await fetchDesks();
      toast.success(t('weekdaysUpdated', { desk: deskId }));
    } catch {
      toast.error(t('error'));
    }
  };

  const renderDeskCard = (deskId) => {
    const desk = desks.find((d) => d.desk_id === deskId) || { desk_id: deskId, is_available: true };
    const weekdays = desk.available_weekdays || [0, 1, 2, 3, 4];
    const dayLabels = t('dayShort');
    return (
      <div
        key={desk.desk_id}
        data-testid={`desk-toggle-${desk.desk_id}`}
        className={`rounded-2xl p-4 ${
          desk.is_available
            ? 'bg-emerald-50 border-2 border-emerald-200'
            : 'bg-slate-100 border-2 border-slate-200'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-bold text-slate-900">{t('desk')} {desk.desk_id}</p>
            <p className={`text-sm ${desk.is_available ? 'text-emerald-600' : 'text-slate-400'}`}>
              {desk.is_available ? t('active') : t('disabled')}
            </p>
          </div>
          <Switch
            checked={desk.is_available}
            onCheckedChange={(checked) => handleDeskToggle(desk.desk_id, checked)}
            data-testid={`desk-switch-${desk.desk_id}`}
          />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <Monitor className="w-4 h-4" />
            <span>{t('monitor')}</span>
          </div>
          <Switch
            checked={desk.has_monitor || false}
            onCheckedChange={(checked) => handleDeskMonitorToggle(desk.desk_id, checked)}
            data-testid={`desk-monitor-switch-${desk.desk_id}`}
          />
        </div>
        <div className="pt-3 mt-2 border-t border-slate-200">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            {t('availableWeekdays')}
          </p>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map((dayIndex) => {
              const on = weekdays.includes(dayIndex);
              return (
                <button
                  key={dayIndex}
                  type="button"
                  onClick={() => handleWeekdayToggle(desk.desk_id, dayIndex, weekdays)}
                  data-testid={`desk-${desk.desk_id}-weekday-${dayIndex}`}
                  aria-pressed={on}
                  className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-colors ${
                    on
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-white text-slate-400 border border-slate-200 line-through hover:bg-slate-50'
                  }`}
                >
                  {dayLabels[dayIndex]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-desks-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Card className="card-floating">
          <CardHeader>
            <CardTitle className="text-xl font-extrabold text-slate-900">
              {t('manageDesks')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 mb-6">
              {t('toggleAvailability')}
            </p>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 md:grid-flow-col gap-4">
                {[1, 2, 3, 4, 5, 6].map(renderDeskCard)}
              </div>

              <div className="text-center text-slate-400 text-xs md:text-sm border-t border-b border-dashed border-slate-300 py-2">
                {t('corridor')}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 md:grid-flow-col gap-4">
                {[7, 8, 9, 10, 11, 12].map(renderDeskCard)}
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
